var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import validatorjs from 'validatorjs';
import * as Sentry from '@sentry/browser';
import { observable, action, when, computed, makeObservable } from 'mobx';
import _find from 'lodash/find';
import VehicleInfoAPI from '../services/VehicleInfoAPI';
import ProfileAPI from '../services/ProfileAPI';
import { i18nextInstance } from '../../i18n';
import { DEFAULT_LANG, LOCAL_STORAGE_KEY } from '../framework/constants/app';
import { transformManufacturers, transformManufacturersMobx, transformModels, transformModelsGeneral, transformYearsAndMonths } from '../framework/DataTransformers';
import { ShowNotificationError } from '../framework/ErrorLogger';
import config from '../../config/app.config';
import { RewriteFrames } from '@sentry/integrations';
class AppStore {
    constructor() {
        Object.defineProperty(this, "language", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_LANG
        });
        Object.defineProperty(this, "manufacturers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "models", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "countries", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "activeManufacturer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "activeModel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "modals", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.map({})
        });
        Object.defineProperty(this, "countriesLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "manufacturersLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "manufacturersCachedWithOnlineOnly", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "modelsLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isNavigationPanelOpened", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isUserMenuOpened", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "toggleNavigationPanel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isNavigationPanelOpened = !this.isNavigationPanelOpened;
                this.isUserMenuOpened = false;
            }
        });
        Object.defineProperty(this, "toggleUserMenu", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isUserMenuOpened = !this.isUserMenuOpened;
                this.isNavigationPanelOpened = false;
            }
        });
        Object.defineProperty(this, "changeLang", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (lang) => {
                const oldLang = i18nextInstance.language;
                return new Promise((resolve, reject) => {
                    if (oldLang !== lang) {
                        i18nextInstance.changeLanguage(lang, (error) => {
                            if (error) {
                                console.log('Something went wrong loading', error);
                                reject(error);
                                return false;
                            }
                            localStorage.setItem(LOCAL_STORAGE_KEY.i18n_KEY, lang);
                            console.group('LANG_CHANGED');
                            console.log('current: ', oldLang);
                            console.log('new: ', lang);
                            console.groupEnd();
                        });
                    }
                    // Set our current system lang for validator
                    // to see all our custom error messages in appropriate lang.
                    validatorjs.useLang(lang);
                    this.language = lang;
                    resolve(lang);
                });
            }
        });
        Object.defineProperty(this, "requestManufacturers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (onlyWithOnlineCars) => {
                this.manufacturersLoading = true;
                this.manufacturersCachedWithOnlineOnly = !!onlyWithOnlineCars;
                return VehicleInfoAPI.getManufacturersInfo(onlyWithOnlineCars)
                    .then((response) => {
                    this.manufacturersLoading = false;
                    return this.manufacturers = response || {};
                })
                    .catch((_error) => {
                    this.manufacturersLoading = false;
                    ShowNotificationError('errors.loadManufacturersInformation');
                });
            }
        });
        Object.defineProperty(this, "getManufacturers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (onlyWithOnlineCars) => new Promise((resolve, _reject) => {
                const paramChanged = this.manufacturersLength && !!onlyWithOnlineCars !== this.manufacturersCachedWithOnlineOnly;
                if (paramChanged) {
                    this.manufacturers = {};
                }
                if (!this.manufacturersLoading && this.manufacturersLength) {
                    resolve(transformManufacturers(this.manufacturers));
                }
                else if (!this.manufacturersLoading && !this.manufacturersLength) {
                    this.requestManufacturers(onlyWithOnlineCars);
                }
                when(() => !this.manufacturersLoading && !!this.manufacturersLength, () => resolve(transformManufacturers(this.manufacturers)));
            })
        });
        Object.defineProperty(this, "getModels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (manufacturerId, year, month) => {
                this.modelsLoading = true;
                return VehicleInfoAPI.getModelsInfo(manufacturerId, year, month)
                    .then((response) => {
                    this.modelsLoading = false;
                    return this.models = transformModels(response);
                })
                    .catch((_error) => {
                    this.modelsLoading = false;
                    ShowNotificationError('errors.loadModelsInformation');
                });
            }
        });
        Object.defineProperty(this, "requestCountries", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.countriesLoading = true;
                return ProfileAPI.getCountries(this.language)
                    .then((response) => {
                    this.countriesLoading = false;
                    return this.countries = response;
                })
                    .catch((_error) => {
                    this.countriesLoading = false;
                });
            }
        });
        Object.defineProperty(this, "getCountries", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => new Promise((resolve, _reject) => {
                if (!this.countriesLoading && this.countriesLength) {
                    resolve(this.countries);
                    return false;
                }
                if (!this.countriesLoading && !this.countriesLength) {
                    this.requestCountries();
                }
                when(() => !this.countriesLoading && !!this.countriesLength, () => resolve(this.countries));
            })
        });
        Object.defineProperty(this, "showModal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id, props) => {
                this.modals.set(id, props);
            }
        });
        Object.defineProperty(this, "hideModal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => {
                if (id && id.length) {
                    this.modals.delete(id);
                }
                else {
                    const iterator = this.modals.entries();
                    let lastModalKey = '';
                    for (let i = 0; i < this.modals.size; i = i + 1) {
                        const modal = iterator.next();
                        if (!modal.done) {
                            lastModalKey = modal.value[0];
                        }
                        else {
                            this.modals.delete(lastModalKey);
                        }
                    }
                }
            }
        });
        Object.defineProperty(this, "hideAllModals", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.modals.clear();
            }
        });
        Object.defineProperty(this, "getManufacturersInfoMobx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => new Promise((resolve, _reject) => {
                if (this.manufacturersLength && this.manufacturersCachedWithOnlineOnly) {
                    this.manufacturers = {};
                }
                if (!this.manufacturersLoading && this.manufacturersLength) {
                    resolve(transformManufacturersMobx(this.manufacturers));
                }
                else if (!this.manufacturersLoading && !this.manufacturersLength) {
                    this.requestManufacturers();
                }
                when(() => !this.manufacturersLoading && !!this.manufacturersLength, () => resolve(transformManufacturersMobx(this.manufacturers)));
            })
        });
        Object.defineProperty(this, "getYearsInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (makerId, modelId) => {
                const markLabel = (_find(transformManufacturersMobx(this.manufacturers), { 'value': makerId }) || {}).label;
                const modelLabel = (_find(this.models, { 'value': modelId }) || {}).label;
                return VehicleInfoAPI.getYearsInfo(makerId, modelId)
                    .then((res) => {
                    this.activeManufacturer = markLabel;
                    this.activeModel = modelLabel;
                    return transformYearsAndMonths(res);
                })
                    .catch((error) => { console.error('getYearsInfo error:', error); ShowNotificationError(); });
            }
        });
        Object.defineProperty(this, "handleSentryInit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                // This function is needed to replace the file path, so that Sentry can access
                // these files from its artifact repository, not from an Internet URL.
                const framesModified = (frame) => {
                    if (frame.filename) {
                        frame.filename = frame.filename.replace(/^.*(?=assets)/g, 'app:///public/');
                    }
                    return frame;
                };
                const sentryConfig = {
                    beforeSend(event, hint) {
                        if (hint.originalException === 'Timeout')
                            return null;
                        return event;
                    },
                    // ADV-290: AuthStore rethrows every Firebase auth failure (`throw new Error(error)`) and
                    // the login/reset forms leave that rejection unhandled. These codes are the user's own
                    // input (unknown email, wrong password, …) and are already shown in the form — not bugs.
                    ignoreErrors: [
                        /Firebase: Error \(auth\/(user-not-found|wrong-password|invalid-email|invalid-credential|user-disabled|too-many-requests|email-already-in-use|weak-password)\)/
                    ],
                    dsn: config.sentry
                };
                if (process.env.RELEASE)
                    sentryConfig.release = process.env.RELEASE;
                if (process.env.NODE_ENV !== 'development') {
                    sentryConfig.integrations = [new RewriteFrames({
                            iteratee: framesModified
                        })];
                }
                Sentry.init(sentryConfig);
            }
        });
        // hideSpecialModels: strips the "Sonstige"/"default" placeholder rows — passed true from the
        // customer-facing search dropdowns (HP/Landing/SRL/Detailsuche), left undefined everywhere
        // else (e.g. VehicleEditPage's model picker) so those keep seeing the full real list.
        Object.defineProperty(this, "getModelsInfoByMaker", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (maker, onlyWithOnlineCars, hideSpecialModels) => VehicleInfoAPI.getModelsInfoByMaker(maker, onlyWithOnlineCars)
                .then((res) => transformModelsGeneral(res, hideSpecialModels))
                .catch((_error) => ShowNotificationError())
        });
        Object.defineProperty(this, "getModelsInfoByMakerMobx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (maker) => VehicleInfoAPI.getModelsInfoByMaker(maker)
                .then((res) => transformModels(res))
        });
        makeObservable(this);
    }
    get manufacturersLength() {
        const { top = [], regular = [] } = this.manufacturers || {};
        return top.length + regular.length;
    }
    get countriesLength() { return this.countries.length; }
    get activeManufacturerInfoMobx() { return this.activeManufacturer; }
    get activeModelInfoMobx() { return this.activeModel; }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "language", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "manufacturers", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], AppStore.prototype, "models", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], AppStore.prototype, "countries", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], AppStore.prototype, "activeManufacturer", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], AppStore.prototype, "activeModel", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "modals", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "countriesLoading", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "manufacturersLoading", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "modelsLoading", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "isNavigationPanelOpened", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AppStore.prototype, "isUserMenuOpened", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AppStore.prototype, "manufacturersLength", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AppStore.prototype, "countriesLength", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AppStore.prototype, "activeManufacturerInfoMobx", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AppStore.prototype, "activeModelInfoMobx", null);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "toggleNavigationPanel", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "toggleUserMenu", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "changeLang", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "getManufacturers", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "getModels", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "getCountries", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "showModal", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "hideModal", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "hideAllModals", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "getManufacturersInfoMobx", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "getYearsInfo", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "handleSentryInit", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "getModelsInfoByMaker", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AppStore.prototype, "getModelsInfoByMakerMobx", void 0);
export default new AppStore();
